/**
 * space_war_contract.ts — the fleet war's escorts under the CAPITALS' contract (space campaign slice C, 2026-09-30).
 *
 * THE PENDING-DEATH LAW (report §9.3, fleet_battle §5.5a/§9): under a server roster no local event kills a war escort.
 * A lethal blow puts the frigate / corvette in a PENDING state — hull held at 1 hp, engines and guns out (the hull's
 * existing `slowUntil` / `jamUntil` subsystem fields, which the AI loop already honours), embers — and a blow that was
 * MINE sends ONE claim. The death arrives only when the roster row flips (every client kills that slot together). A
 * refused claim, or no flip within PENDING_EXPIRE_MS, RESTORES the hull smoothly (hp ramps back over RESTORE_MS) — the
 * picture returns to the server's truth instead of keeping a ghost dead here. A forced window (no row) keeps the old
 * local authority. Pure state + a WeakMap store keyed by the hull object (a rebuilt battle's hulls are new objects, so
 * nothing stale survives a 60 km re-entry, a reconnect or a rebase).
 */

export const PENDING_EXPIRE_MS = 12_000;
export const RESTORE_MS = 3_000;
export const RESTORE_HP_FRAC = 0.35;
/** A refusal that can change (out of the zone, out of flight, the minute's budget) re-arms the claim; one the record
 *  will never take (own banner, no such slot, already down, on the record, the war over) does not — the capitals' rule. */
export const RETRYABLE_REFUSAL = /outside the war zone|flight|system|minute/i;

export type PendingPhase = 'pending' | 'restoring';
export interface EscortPending {
  phase: PendingPhase;
  since: number;
  claimSent: boolean;
  claimBlocked: boolean;
  outcome: 'none' | 'sent' | 'accepted' | 'refused' | 'expired';
  reason: string;
  restoreAt: number;
  restoreFrom: number;
  restoreTo: number;
  /** The last ember (the renderer's cadence). */
  fxAt?: number;
}

/** A lethal blow landed. `rowAlive` = the roster row's life (null = no row for this slot). */
export function onLethal(
  state: EscortPending | undefined,
  c: { rosterMode: boolean; rowAlive: boolean | null; byPlayer: boolean; now: number },
): { next: EscortPending | undefined; action: 'kill' | 'pending'; sendClaim: boolean } {
  if (!c.rosterMode) return { next: undefined, action: 'kill', sendClaim: false }; // a forced window: the picture decides
  if (c.rowAlive === false) return { next: undefined, action: 'kill', sendClaim: false }; // the roster already buried it
  const base: EscortPending = state && state.phase === 'pending'
    ? state
    : { phase: 'pending', since: c.now, claimSent: state?.claimSent ?? false, claimBlocked: state?.claimBlocked ?? false, outcome: state?.outcome ?? 'none', reason: state?.reason ?? '', restoreAt: 0, restoreFrom: 1, restoreTo: 1 };
  const sendClaim = c.byPlayer && !base.claimSent && !base.claimBlocked;
  const next: EscortPending = { ...base, claimSent: base.claimSent || sendClaim, outcome: sendClaim ? 'sent' : base.outcome };
  return { next, action: 'pending', sendClaim };
}

/** The record answered my claim. Accepted → keep waiting for the row; refused → restore (and re-arm only when the
 *  refusal can change). */
export function onClaimResult(state: EscortPending, ok: boolean, reason: string, now: number, maxHp: number): EscortPending {
  if (ok) return { ...state, outcome: 'accepted', reason: '' };
  const retry = RETRYABLE_REFUSAL.test(reason);
  return { ...state, phase: 'restoring', outcome: 'refused', reason, claimSent: retry ? false : state.claimSent, claimBlocked: retry ? state.claimBlocked : true, restoreAt: now, restoreFrom: 1, restoreTo: Math.max(1, Math.round(maxHp * RESTORE_HP_FRAC)) };
}

/** Once a second (updateWarEscorts): what the hull should be now. `kill` = the roster flipped; `hp` = set the hull's
 *  hp; `done` = the restore finished (forget the state); `disabled` = engines/guns held out. */
export function tickPending(state: EscortPending, rowAlive: boolean | null, now: number, maxHp: number): { next: EscortPending | undefined; kill: boolean; hp: number | null; disabled: boolean } {
  if (rowAlive === false) return { next: undefined, kill: true, hp: null, disabled: false };
  let s = state;
  if (s.phase === 'pending' && now - s.since >= PENDING_EXPIRE_MS) {
    s = { ...s, phase: 'restoring', outcome: s.outcome === 'accepted' ? 'expired' : (s.outcome === 'none' || s.outcome === 'sent' ? 'expired' : s.outcome), reason: s.reason || 'no death on the roster', restoreAt: now, restoreFrom: 1, restoreTo: Math.max(1, Math.round(maxHp * RESTORE_HP_FRAC)), claimSent: false };
  }
  if (s.phase === 'pending') return { next: s, kill: false, hp: 1, disabled: true };
  const u = Math.max(0, Math.min(1, (now - s.restoreAt) / RESTORE_MS));
  const hp = Math.round(s.restoreFrom + (s.restoreTo - s.restoreFrom) * (u * u * (3 - 2 * u)));
  if (u >= 1) return { next: undefined, kill: false, hp, disabled: false };
  return { next: s, kill: false, hp, disabled: false };
}

// ── The store: one pending record per hull object ────────────────────────────
const pendings = new WeakMap<object, EscortPending>();
const live = new Set<{ key: string; hull: WeakRef<object> }>();
export function pendingOf(hull: object): EscortPending | undefined { return pendings.get(hull); }
export function setPending(hull: object, key: string, s: EscortPending | undefined): void {
  if (s) {
    if (!pendings.has(hull)) live.add({ key, hull: new WeakRef(hull) });
    pendings.set(hull, s);
  } else {
    pendings.delete(hull);
    for (const e of live) if (e.hull.deref() === hull || !e.hull.deref()) live.delete(e);
  }
}
/** Every hull still pending or restoring (for the HUD line and the probe), keyed by its roster key. */
export function pendingList(): Array<{ key: string; state: EscortPending }> {
  const out: Array<{ key: string; state: EscortPending }> = [];
  for (const e of live) {
    const h = e.hull.deref();
    const s = h ? pendings.get(h) : undefined;
    if (!h || !s) { live.delete(e); continue; }
    out.push({ key: e.key, state: s });
  }
  return out;
}
/** The HUD fragment: `· pending: Compact frigate #1 (at the record)` / `(refused — fights on)`. */
export function pendingHud(list: ReadonlyArray<{ key: string; state: EscortPending }>): string {
  if (!list.length) return '';
  const name = (k: string): string => {
    const m = /^war([01]):(fri|cor)(\d+)$/.exec(k);
    return m ? `${m[1] === '0' ? 'Concord' : 'Compact'} ${m[2] === 'fri' ? 'frigate' : 'corvette'} #${Number(m[3]) + 1}` : k;
  };
  const one = (e: { key: string; state: EscortPending }): string => `${name(e.key)} ${e.state.phase === 'pending'
    ? (e.state.outcome === 'accepted' ? '(the record took it — dying)' : e.state.outcome === 'sent' ? '(claim at the record)' : '(burning — awaiting the roster)')
    : `(${e.state.outcome === 'refused' ? 'the record refused it' : 'no death on the roster'} — it fights on)`}`;
  return ` · ⚠ ${list.slice(0, 2).map(one).join(', ')}${list.length > 2 ? ` +${list.length - 2}` : ''}`;
}

// ── The probe's campaign override (dev only) ─────────────────────────────────
/** A probe points the war picture at a SANDBOX campaign (a sentinel cycle the clock never reaches) so the roster
 *  contract can be pictured at any minute of the hour without touching the live war. Null = the clock's campaign. */
let override: bigint | null = null;
export function setCampaignOverride(id: bigint | null): void { override = id; }
export function campaignOverride(): bigint | null { return override; }

export interface WarContractProbeApi {
  /** Deal a lethal PLAYER blow to a named war escort (through the real war branch). Returns what happened. */
  strike(key: string, byPlayer?: boolean): string;
  /** The escorts in the picture: key, hp, pending phase / outcome. */
  escorts(): Array<{ key: string; hp: number; dead: boolean; phase: string; outcome: string; reason: string; abs: [number, number, number]; disabled: boolean }>;
}
let probeApi: WarContractProbeApi | null = null;
/** Installed once from SpaceModule.updateWarEscorts (dev builds / probes only read it). */
export function installWarContractProbe(api: WarContractProbeApi): void {
  if (probeApi) { probeApi = api; return; }
  probeApi = api;
  (globalThis as unknown as { __warContract?: unknown }).__warContract = {
    override: (id: string | number | bigint | null) => { setCampaignOverride(id === null ? null : BigInt(id)); return String(override); },
    strike: (key: string, byPlayer = true) => probeApi?.strike(key, byPlayer) ?? 'no battle',
    escorts: () => probeApi?.escorts() ?? [],
    pending: () => pendingList().map((e) => ({ key: e.key, ...e.state })),
  };
}
